import React from "react";
import { useCustomer, useAuth } from "contexts/api";
import type { Excursion, ExcursionGroup, GroupLabels } from "api/excursions/Excursion.typed";
import type { ZekayCustomer } from "api/customers/Customer.typed";
import { normalizeCustomerEmoji, rawCustomerEmojiFromApi } from "../../../common/customerEmoji";
import { formatInProgressTrainingsSessionsTooltip } from "../../shared/utils/excursionInProgressTrainingsTooltip";

export function useExcursionLabels(
  excursions: Excursion[],
  bookingUserNameMap?: Record<number, string>,
) {
  const { getCustomersBatch } = useCustomer();
  const { getUsersDisplayBatch } = useAuth();

  const userIdsFromExcursions = React.useMemo(() => {
    const ids = new Set<number>();
    excursions.forEach((ex) => {
      (ex.groups ?? []).forEach((g) => {
        (g.excursion_users ?? []).forEach((u) => ids.add(u.id));
      });
    });
    return Array.from(ids);
  }, [excursions]);

  const [userDetailsMap, setUserDetailsMap] = React.useState<
    Record<number, { firstName?: string; lastName?: string; qualification?: string }>
  >({});

  React.useEffect(() => {
    if (userIdsFromExcursions.length === 0) {
      setUserDetailsMap({});
      return;
    }
    let cancelled = false;
    void getUsersDisplayBatch(userIdsFromExcursions).then((list) => {
      if (cancelled) return;
      if (!list) {
        setUserDetailsMap({});
        return;
      }
      const dMap: Record<
        number,
        { firstName?: string; lastName?: string; qualification?: string }
      > = {};
      list.forEach((row) => {
        if (!Number.isFinite(row.id)) return;
        dMap[row.id] = {
          firstName: row.first_name?.trim(),
          lastName: row.last_name?.trim(),
          qualification: row.last_qualification?.trim(),
        };
      });
      setUserDetailsMap(dMap);
    });
    return () => {
      cancelled = true;
    };
  }, [userIdsFromExcursions, getUsersDisplayBatch]);

  const [customerNameMap, setCustomerNameMap] = React.useState<Record<number, string>>({});
  const [customerDetailsMap, setCustomerDetailsMap] = React.useState<
    Record<
      number,
      { firstName?: string; lastName?: string; qualification?: string; emoji?: string }
    >
  >({});
  const [rawCustomers, setRawCustomers] = React.useState<ZekayCustomer[]>([]);

  const customerIdsFromExcursions = React.useMemo(() => {
    const ids = new Set<number>();
    excursions.forEach((ex) => {
      (ex.groups ?? []).forEach((g) => {
        (g.excursion_customers ?? []).forEach((c) => ids.add(c.id));
      });
    });
    return Array.from(ids);
  }, [excursions]);

  React.useEffect(() => {
    if (customerIdsFromExcursions.length === 0) {
      setCustomerNameMap({});
      setCustomerDetailsMap({});
      setRawCustomers([]);
      return;
    }
    getCustomersBatch(customerIdsFromExcursions).then((data) => {
      const list = Array.isArray(data) ? data : [];
      const cMap: Record<number, string> = {};
      const dMap: Record<
        number,
        { firstName?: string; lastName?: string; qualification?: string; emoji?: string }
      > = {};
      list.forEach((c) => {
        if (c.Id != null) {
          cMap[c.Id] = [c.FirstName, c.LastName].filter(Boolean).join(" ") || `#${c.Id}`;
          const em = normalizeCustomerEmoji(c.Emoji ?? undefined);
          dMap[c.Id] = {
            firstName: c.FirstName?.trim(),
            lastName: c.LastName?.trim(),
            qualification: c.LastQualification?.trim(),
            ...(em ? { emoji: em } : {}),
          };
        }
      });
      setCustomerNameMap(cMap);
      setCustomerDetailsMap(dMap);
      setRawCustomers(list);
    });
  }, [customerIdsFromExcursions, getCustomersBatch]);

  const getGroupLabels = React.useCallback(
    (ex: Excursion): GroupLabels[] =>
      (ex.groups ?? []).map((g: ExcursionGroup) => {
        const firstCustomerId = g.excursion_customers?.[0]?.id;
        const userId = g.excursion_users?.[0]?.id;
        const firstCustomerDetails =
          firstCustomerId != null ? customerDetailsMap[firstCustomerId] : undefined;
        const userDetails = userId != null ? userDetailsMap[userId] : undefined;
        const fromBatchName =
          userDetails &&
          [userDetails.firstName, userDetails.lastName].filter(Boolean).join(" ").trim();
        const customers = (g.excursion_customers ?? []).map((c) => {
          const d = customerDetailsMap[c.id];
          const emFromCustomer = normalizeCustomerEmoji(d?.emoji);
          const emLegacy = normalizeCustomerEmoji(rawCustomerEmojiFromApi(c));
          const em = emFromCustomer ?? emLegacy;
          return {
            id: c.id,
            first_name: d?.firstName,
            last_name: d?.lastName,
            qualification: d?.qualification,
            name: customerNameMap[c.id],
            ...(em ? { emoji: em } : {}),
          };
        });
        const guides = (g.excursion_users ?? []).map((u) => {
          const d = userDetailsMap[u.id];
          const composed = d && [d.firstName, d.lastName].filter(Boolean).join(" ").trim();
          return {
            id: u.id,
            first_name: d?.firstName,
            last_name: d?.lastName,
            qualification: d?.qualification,
            name: bookingUserNameMap?.[u.id]?.trim() || composed || undefined,
          };
        });
        return {
          user_name:
            userId != null
              ? bookingUserNameMap?.[userId]?.trim() || fromBatchName || undefined
              : undefined,
          user_first_name: userDetails?.firstName,
          user_last_name: userDetails?.lastName,
          user_qualification: userDetails?.qualification,
          customer_name: firstCustomerId != null ? customerNameMap[firstCustomerId] : undefined,
          customer_last_name: firstCustomerDetails?.lastName,
          customer_first_name: firstCustomerDetails?.firstName,
          customer_qualification: firstCustomerDetails?.qualification,
          customers,
          guides,
        };
      }),
    [userDetailsMap, bookingUserNameMap, customerNameMap, customerDetailsMap],
  );

  const getCustomerInProgressTrainingsTooltip = React.useCallback(
    (customerId: number): string | null => {
      const c = rawCustomers.find((x) => x.Id === customerId);
      return formatInProgressTrainingsSessionsTooltip(c?.Trainings);
    },
    [rawCustomers],
  );

  return { getGroupLabels, rawCustomers, getCustomerInProgressTrainingsTooltip };
}
